import { apcaContrast, hexToOklch, oklchToHex, parseHex } from "../color/oklch.js";
import { ensureDesignTokenMaps } from "../dsl/tokens.js";
import { canvasDensityScale } from "../dsl/format-catalog.js";
/**
 * Merges a restaurant's brand into a template's token table and guarantees the
 * result is still readable.
 *
 * Two things make this more than a spread: the brand supplies one or two
 * colours and the rest of the palette has to be derived from them in a way that
 * stays coherent, and any pairing the template declared as text-on-background
 * must survive that derivation. Restaurants pick brand colours for signage, not
 * for 14px descriptions.
 */
export function resolveTokens(template, brand) {
    const diagnostics = [];
    const sourceTokens = ensureDesignTokenMaps(template.tokens);
    const colors = { ...sourceTokens.colors };
    const brandable = template.brandable ?? {
        colors: [],
        fonts: [],
    };
    const brandableColors = new Set(brandable.colors);
    // 1. Explicit per-token overrides win outright; the operator picked them in
    //    Brand Studio while looking at a live preview.
    for (const [key, value] of Object.entries(brand?.colorOverrides ?? {})) {
        if (!brandableColors.has(key))
            continue;
        if (!parseHex(value))
            continue;
        colors[key] = value;
    }
    // 2. A single primary colour drives the accent, which is the token templates
    //    lean on for emphasis.
    const explicitAccent = brand?.colorOverrides?.accent;
    if (brand?.primaryColor && brandableColors.has('accent') && !explicitAccent) {
        const parsed = hexToOklch(brand.primaryColor);
        if (parsed)
            colors.accent = oklchToHex(parsed);
    }
    if (brand?.secondaryColor &&
        brandableColors.has('surfaceAlt') &&
        !brand.colorOverrides?.surfaceAlt) {
        const parsed = hexToOklch(brand.secondaryColor);
        // Used as a large background, so it is pulled towards the page's own
        // lightness instead of being applied at full strength.
        if (parsed) {
            const surface = hexToOklch(colors.surface ?? '#ffffff');
            const targetLightness = surface ? surface.l + (parsed.l - surface.l) * 0.25 : parsed.l;
            colors.surfaceAlt = oklchToHex({ l: targetLightness, c: parsed.c * 0.35, h: parsed.h });
        }
    }
    // 3. Derive the foreground that sits on the accent, rather than trusting the
    //    template's default to still work against a new brand colour.
    if (colors.accent) {
        colors.onAccent = pickReadableForeground(colors.accent);
    }
    // 4. Repair every declared pairing.
    for (const rule of template.contrastRules ?? []) {
        const background = colors[rule.background];
        const text = colors[rule.text];
        if (!background || !text)
            continue;
        const measured = Math.abs(apcaContrast(text, background));
        if (measured >= rule.minLc)
            continue;
        const repaired = repairContrast(text, background, rule.minLc);
        if (repaired) {
            colors[rule.text] = repaired;
            diagnostics.push({
                level: 'info',
                code: 'contrast-repaired',
                message: `Adjusted "${rule.text}" on "${rule.background}" from Lc ${measured.toFixed(0)} to ${Math.abs(apcaContrast(repaired, background)).toFixed(0)}`,
            });
        }
        else {
            diagnostics.push({
                level: 'warning',
                code: 'contrast-repaired',
                message: `Could not reach Lc ${rule.minLc} for "${rule.text}" on "${rule.background}" (best ${measured.toFixed(0)})`,
            });
        }
    }
    // 5. Typography, spacing and radius scaling.
    const typeScale = brandable.allowTypeScale ? clamp(brand?.typeScale ?? 0, -1, 1) : 0;
    const radiusScale = brandable.allowRadiusScale
        ? clamp(brand?.radiusScale ?? 0, -1, 1)
        : 0;
    const duScale = canvasDensityScale(template.canvas);
    // A full unit maps to +/-12%: enough to feel like a choice, small enough that
    // a blueprint's capacity estimates stay valid.
    const typeFactor = 1 + typeScale * 0.12;
    const typography = {};
    for (const [key, token] of Object.entries(sourceTokens.typography)) {
        typography[key] = {
            ...token,
            size: round(token.size * typeFactor * duScale),
            minSize: token.minSize === undefined ? undefined : round(token.minSize * typeFactor * duScale),
            maxSize: token.maxSize === undefined ? undefined : round(token.maxSize * typeFactor * duScale),
        };
    }
    const radiusFactor = 1 + radiusScale * 0.6;
    const radii = {};
    for (const [key, value] of Object.entries(sourceTokens.radii)) {
        // Leave a deliberate hard corner alone; scaling zero is still zero, but a
        // pill must not become a slightly-rounded rectangle either.
        radii[key] = value >= 999 ? value : round(value * radiusFactor * duScale);
    }
    const spacing = {};
    for (const [key, value] of Object.entries(sourceTokens.spacing)) {
        spacing[key] = round(value * duScale);
    }
    const borders = {};
    for (const [key, border] of Object.entries(sourceTokens.borders)) {
        borders[key] = { ...border, width: round(border.width * duScale) };
    }
    const shadows = {};
    for (const [key, shadow] of Object.entries(sourceTokens.shadows)) {
        shadows[key] = {
            ...shadow,
            offsetX: round(shadow.offsetX * duScale),
            offsetY: round(shadow.offsetY * duScale),
            blur: round(shadow.blur * duScale),
            spread: round(shadow.spread * duScale),
        };
    }
    // 6. Font substitutions.
    const fonts = { ...sourceTokens.fonts };
    const allowedFonts = new Set(brandable.fonts);
    const substitutions = [
        ['display', brand?.displayFont],
        ['body', brand?.bodyFont],
        ['accent', brand?.accentFont],
    ];
    for (const [role, family] of substitutions) {
        if (!family || !allowedFonts.has(role))
            continue;
        fonts[role] = { ...fonts[role], family, source: 'google' };
    }
    const resolved = {
        colors,
        typography,
        spacing,
        radii,
        borders,
        shadows,
        fonts,
    };
    return {
        tokens: { ...resolved, cssVariables: buildCssVariables(resolved), duScale },
        diagnostics,
    };
}
/** Black or white, whichever the accent can actually carry. */
function pickReadableForeground(background) {
    const onWhite = Math.abs(apcaContrast('#FFFFFF', background));
    const onBlack = Math.abs(apcaContrast('#111111', background));
    return onWhite >= onBlack ? '#FFFFFF' : '#111111';
}
/**
 * Walks the text colour's lightness away from the background until the pairing
 * clears the threshold. Hue and chroma are preserved so a brand colour used as
 * text stays recognisably itself.
 */
function repairContrast(text, background, minLc) {
    const textOklch = hexToOklch(text);
    const backgroundOklch = hexToOklch(background);
    if (!textOklch || !backgroundOklch)
        return null;
    const goDarker = backgroundOklch.l > 0.5;
    let best = null;
    for (let step = 1; step <= 40; step += 1) {
        const delta = step * 0.025;
        const lightness = clamp(goDarker ? textOklch.l - delta : textOklch.l + delta, 0, 1);
        const candidate = oklchToHex({ ...textOklch, l: lightness });
        if (Math.abs(apcaContrast(candidate, background)) >= minLc)
            return candidate;
        best = candidate;
        if (lightness <= 0 || lightness >= 1)
            break;
    }
    // Even pure black or white missed the target; hand back the extreme rather
    // than the original, since it is still the most legible option available.
    return best;
}
function buildCssVariables(tokens) {
    const variables = {};
    for (const [key, value] of Object.entries(tokens.colors)) {
        variables[`--mv-color-${kebab(key)}`] = value;
    }
    for (const [key, value] of Object.entries(tokens.spacing)) {
        variables[`--mv-space-${kebab(key)}`] = `${value}px`;
    }
    for (const [key, value] of Object.entries(tokens.radii)) {
        variables[`--mv-radius-${kebab(key)}`] = `${value}px`;
    }
    for (const [role, font] of Object.entries(tokens.fonts)) {
        variables[`--mv-font-${role}`] = `"${font.family}", ${font.fallback}`;
    }
    return variables;
}
const kebab = (value) => value.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
const clamp = (value, min, max) => value < min ? min : value > max ? max : value;
const round = (value) => Math.round(value * 100) / 100;
//# sourceMappingURL=resolve-tokens.js.map